import { JRCIcon } from 'components/JRCIcon/JRCIcon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { paginate } from './pagination';

import {
    WidgetDatasourceValues,
    WidgetTableColumn,
    WidgetTableColumnTypes,
    WidgetTableContent,
} from 'jamespot-user-api';

import { JRCWidgetTableTdRenderer } from './JRCWidgetTableTdRenderer';

import {
    CSSTable,
    CSSTableHead,
    CSSTableBody,
    CSSTableTd,
    CSSTableHeadTdContent,
    CSSTableTdContent,
    CSSTableTr,
    CSSTableOverlay,
    CSSTableWrapper,
    CSSTableTh,
    CSSTableRadius,
} from './JRCWidgetTable.styles';
import { JRCPagination } from 'components/JRCPagination/JRCPagination';

import { SUPPORTED_SORTABLE_TYPE } from './const';

export type SortValues = 'ascending' | 'descending';

export const sort = (
    data: Record<string, WidgetDatasourceValues>[],
    sortBy: SortValues,
    name: string,
    colType: WidgetTableColumnTypes,
) => {
    if (colType === 'number') {
        return data.sort((a, b) =>
            sortBy === 'descending' ? Number(b[name]) - Number(a[name]) : Number(a[name]) - Number(b[name]),
        );
    }

    if (SUPPORTED_SORTABLE_TYPE.includes(colType) && isASortableProperty(data, name)) {
        return data.sort((a, b) =>
            sortBy === 'descending'
                ? b[name].toString().localeCompare(a[name].toString())
                : a[name].toString().localeCompare(b[name].toString()),
        );
    }

    // if not, return data
    return data;
};

export function isASortableProperty(data: Record<string, WidgetDatasourceValues>[], name: string) {
    return data[0] && data[0][name] && (typeof data[0][name] === 'string' || typeof data[0][name] === 'number');
}

export type JRCWidgetTableProps = WidgetTableContent & {
    columns: WidgetTableColumn[];
    rows: Record<string, WidgetDatasourceValues>[];
    tableDefaultSortOrder?: SortValues;
    tableDefaultSortName?: string;
    initialPage?: number;
};

export const JRCWidgetTable = (args: JRCWidgetTableProps) => {
    const {
        rows,
        columns,
        tableBorderRadius = 0,
        initialPage,
        tableDefaultSortOrder = 'ascending',
        tableDefaultSortName = 'unknown',
        tableHeadColor = '#f1f1f1',
        tableHeadTextColor = '#000',
        tableHeadIconColor = '#000',
        tableRowColor = '#f5f5f5',
        tableRowTextColor = '#000',
        tableColumnsData,
        tableSizedColumns,
        tableSizedColumnsWidth,
        limit = 20,
    } = args;
    const [page, setPage] = useState<number>(initialPage ?? 1);
    const [sorting, setSorting] = useState<{ name: string; order: SortValues }>({
        name: tableDefaultSortName,
        order: tableDefaultSortOrder,
    });

    const handleToggleSort = useCallback(
        (name) => {
            if (sorting.name === name) {
                setSorting({ ...sorting, order: sorting.order === 'ascending' ? 'descending' : 'ascending' });
            } else {
                setSorting({ name, order: 'ascending' });
            }
        },
        [sorting],
    );

    const totalRows = useMemo(() => rows.length, [rows]);

    const sortColumnType = useMemo(() => {
        const col = columns.find((col) => col.name === sorting.name);
        return col ? col.type : 'text';
    }, [columns, sorting]);

    const datas = useMemo(
        () => paginate(sort(rows, sorting.order, sorting.name, sortColumnType), page, Number(limit)),
        [rows, sorting.name, sorting.order, page, limit, sortColumnType],
    );

    useEffect(() => {
        const firstSortableColumns = columns?.filter((el) => el.sortable)[0];
        if (firstSortableColumns && sorting.name === 'unknown') {
            setSorting({ name: firstSortableColumns.name, order: 'ascending' });
        }
    }, [columns, sorting.name]);

    return (
        <CSSTableOverlay>
            <CSSTableRadius borderRadius={tableBorderRadius}>
                <CSSTableWrapper>
                    <CSSTable>
                        <CSSTableHead>
                            <CSSTableTr background={tableHeadColor} color={tableHeadTextColor}>
                                {columns.map((col) => {
                                    const isSortable = col.sortable || col.type === 'text';
                                    const isSorted = col.name === sorting.name;
                                    return (
                                        <TableTh
                                            key={col.name}
                                            ariaSort={isSortable ? (isSorted ? sorting.order : 'none') : undefined}
                                            width={tableSizedColumns ? tableSizedColumnsWidth : undefined}
                                            onClick={() => (isSortable ? handleToggleSort(col.name) : undefined)}>
                                            <CSSTableHeadTdContent>
                                                {col.label}
                                                {isSortable && (
                                                    <>
                                                        {isSorted ? (
                                                            <JRCIcon
                                                                aria-hidden="true"
                                                                name={
                                                                    sorting.order === 'ascending'
                                                                        ? 'icon-sort-asc'
                                                                        : 'icon-sort-desc'
                                                                }
                                                                color={tableHeadIconColor}
                                                            />
                                                        ) : (
                                                            <JRCIcon
                                                                aria-hidden="true"
                                                                name={'icon-sort'}
                                                                color={tableHeadIconColor}
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </CSSTableHeadTdContent>
                                        </TableTh>
                                    );
                                })}
                            </CSSTableTr>
                        </CSSTableHead>
                        <CSSTableBody>
                            {datas.map((tr, index) => {
                                return (
                                    <CSSTableTr
                                        key={index}
                                        background={index % 2 === 1 ? tableRowColor : '#fff'}
                                        color={tableRowTextColor}>
                                        {columns.map((td) => {
                                            return (
                                                <CSSTableTd key={`${index}-${td.name}`}>
                                                    <CSSTableTdContent>
                                                        <JRCWidgetTableTdRenderer
                                                            backgroundColor={index % 2 === 1 ? tableRowColor : '#fff'}
                                                            col={td}
                                                            type={td.type}
                                                            value={tr[td.name]}
                                                            name={td.name}
                                                            tableColumnsData={tableColumnsData ?? []}
                                                            url={typeof tr._url === 'string' ? tr._url : undefined}
                                                        />
                                                    </CSSTableTdContent>
                                                </CSSTableTd>
                                            );
                                        })}
                                    </CSSTableTr>
                                );
                            })}
                        </CSSTableBody>
                    </CSSTable>
                    {typeof limit === 'number' && totalRows > limit && (
                        <JRCPagination
                            activePage={page}
                            nbrOfPage={Math.ceil(totalRows / limit)}
                            clickHandler={setPage}
                        />
                    )}
                </CSSTableWrapper>
            </CSSTableRadius>
        </CSSTableOverlay>
    );
};

export const TableTh = ({
    children,
    ariaSort,
    width,
    onClick,
}: {
    children: React.ReactNode;
    ariaSort: 'none' | 'ascending' | 'descending' | 'other' | undefined;
    width?: number;
    onClick: () => void;
}) => {
    return ariaSort ? (
        <CSSTableTh aria-sort={ariaSort} width={width} onClick={onClick}>
            {children}
        </CSSTableTh>
    ) : (
        <CSSTableTh width={width} onClick={onClick}>
            {children}
        </CSSTableTh>
    );
};
